import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, ButtonGroup, Container, Modal, ModalBody, ModalHeader } from 'reactstrap';
import styled from 'styled-components';
import {
    getPhysiotherapyRequest,
    requestPhysiotherapyPayment,
    resendCashlessLetter,
    generateCashlessLetter,
} from '../../../services/api/cashless-physiotherapy-v2';
import ConfirmationModal from '../../common/ConfirmationModal';
import LoadingComponent from '../../common/LoadingComponent';
import { BoldText } from '../../common/Text';
import OrderStatus from '../components/OrderStatus';
import RequestRow from './RequestRow';
import { getBlobUrl } from '../../../utils/getBlobUrlAndContentType';

const DetailsContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
`;

const SectionContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const SectionInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0.625rem;
`;

const SectionHeader = styled(BoldText)`
  font-size: 1.2rem;
  color: #4b4f5b;
`;

const SectionBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-left: 0.625rem;
`;

const SpocDetailContainer = styled(SectionContainer)`
  background-color: #f8f9fa;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  border-radius: 4px;
  padding: 0.625rem;
`;

const PhysiotherapyRequestDetails = ({
    details,
    onClose,
    isOpen
}) => {
    const [vaccineInfo, setVaccineInfo] = useState({})
    const [activeInfo, setActiveInfo] = useState('basic')
    const [basicInfo, setBasicInfo] = useState({})
    const [payInfo, setPayInfo] = useState({})
    const [spocInfo, setSpocInfo] = useState({})
    const [reqPaymentLoading, setReqPaymentLoading] = useState(false)
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [confirmMessage, setConfirmMessage] = useState('')
    const [transactions, setTransactions] = useState([])
    const [generateLetterLoading, setGenerateLetterLoading] = useState(false)

    useEffect(() => {
        if (details.requestId) {
            findRequestInformation(details.requestId)
        }
    }, [details.requestId])

    const findRequestInformation = (requestId) => {
        getPhysiotherapyRequest(requestId)
        .then((response) => {
            setBasicInfo(response.basicInfo)
        })
    }

    const resendPhysiotherapyLetter = () => {
        const { requestId } = details
        return resendCashlessLetter(requestId)
        .then((response) => {
            if (response.message === 'success') {
                return toast.success('Cashless letter resent successfully')
            }
            toast.error('Error while sending cashless letter')
        })
        .catch((err) => {
            console.log({err})
            toast.error('Error while sending cashless letter')
        })
    }

    const renderTimeline = () => (
        <DetailsContainer>
            <SectionContainer>
                <SectionHeader>Physiotherapy Booking Timeline</SectionHeader>
                <SectionBody>
                    <OrderStatus
                        requestId={details.requestId}
                    />
                </SectionBody>
            </SectionContainer>
        </DetailsContainer>
    )

    const renderBasicInfo = () => (
        <DetailsContainer>
            <SectionContainer>
                <SectionInnerContainer>
                    <SectionHeader>User Booking Details</SectionHeader>
                    <SectionBody>
                        <RequestRow name='User Id:' value={basicInfo.userId} />
                        <RequestRow name='Patient Name:' value={basicInfo.patientName} />
                        <RequestRow name='Patient Relation:' value={basicInfo.patientRelation} />
                        <RequestRow name='Patient DOB:' value={basicInfo.patientDob} />
                        <RequestRow name='User Email:' value={basicInfo.userEmail} />
                        <RequestRow name='User Phone:' value={basicInfo.userPhone} />
                    </SectionBody>
                </SectionInnerContainer>
                <SectionInnerContainer>
                    <SectionHeader>Consultation Details</SectionHeader>
                    <SectionBody>
                        <RequestRow name='Appointment Id:' value={basicInfo.requestId} />
                        <RequestRow name='Appointment Date:' value={basicInfo.appointmentDate} />
                        <RequestRow name='Appointment Slot:' value={basicInfo.appointmentTime} />
                        <RequestRow name='Requested Appointment Date:' value={basicInfo.requestedAppointmentDate} />
                        <RequestRow name='Requested Appointment Slot:' value={basicInfo.requestedAppointmentTime} />
                        <RequestRow name='Consultation Amount:' value={basicInfo.consultationAmount} />
                        <RequestRow name='Wallet Deduction:' value={basicInfo.walletDeduction} />
                        <RequestRow name='Copay:' value={basicInfo.consultationCopay} />
                        <RequestRow name='Wallet Refund:' value={basicInfo.walletRefundAmount != null ? `₹${basicInfo.walletRefundAmount}` : null} />
                        <RequestRow name='Copay Refund:' value={basicInfo.copayRefundAmount != null ? `₹${basicInfo.copayRefundAmount}` : null} />
                        {basicInfo.digitizationRejectedReason && (
                            <RequestRow name='Digitization Rejected Reason:' value={basicInfo.digitizationRejectedReason} />
                        )}
                    </SectionBody>
                </SectionInnerContainer>
            </SectionContainer>
            <SectionContainer>
                <SectionInnerContainer>
                <SectionHeader>Physiotherapy Booking Details</SectionHeader>
                <SectionBody>
                    <RequestRow name='Center Id' value={basicInfo.centerId} />
                    <RequestRow name='Center Name:' value={basicInfo.centerName} />
                    <RequestRow
                        name={basicInfo.homeVisit === 1 ? 'User Address:' : 'Center Address:'}
                        value={basicInfo.homeVisit === 1 ? basicInfo.userAddress : basicInfo.centerAddress}
                    />
                    {basicInfo.homeVisit === 1 && (
                        <RequestRow name='User Pincode:' value={basicInfo.userPincode} />
                    )}
                </SectionBody>
                </SectionInnerContainer>

                <SectionInnerContainer>
                <SectionHeader>Session Details</SectionHeader>
                <SectionBody>
                    <RequestRow name='Request Valid Till:' value={basicInfo.validTill} />
                        <RequestRow name='Sessions Approved:' value={basicInfo.approvedSessions} />
                    <RequestRow name='Package Name:' value={basicInfo.packageName} />
                        <RequestRow name='Sessions Booked:' value={basicInfo.bookedSessions} />
                        <RequestRow name='Sessions Taken:' value={basicInfo.sessionsTaken} />
                        <RequestRow name='Cart Amount:' value={basicInfo.cartAmount} />
                        <RequestRow name='Final Cart Amount:' value={basicInfo.finalCartAmount} />
                        <RequestRow name='Wallet Deduction:' value={basicInfo.cartWalletDeduction} />
                        <RequestRow name='Copay:' value={basicInfo.cartCopay} />
                        {basicInfo.additionalDiscountInfo && (
                            <>
                                <RequestRow
                                    name='Additional Discount:'
                                    value={basicInfo.additionalDiscountInfo.amount != null ? `₹${basicInfo.additionalDiscountInfo.amount}` : null}
                                />
                                <RequestRow
                                    name='Additional Discount Status:'
                                    value={basicInfo.additionalDiscountInfo.status}
                                />
                            </>
                        )}

                </SectionBody>
                </SectionInnerContainer>

                <SectionInnerContainer>
                <SectionHeader>User Wallet Amount</SectionHeader>
                <SectionBody>
                    <RequestRow name='Available Wallet Amount:' value={basicInfo.userWalletBalance} />
                </SectionBody>
                </SectionInnerContainer>
            </SectionContainer>
        </DetailsContainer>
    )

    const yesNo = value => value ? 'Yes' : 'No'

    const renderCenterInfo = () => (
        <DetailsContainer>
            <SectionContainer>
                <SectionInnerContainer>
                    <SectionHeader>Center Details:</SectionHeader>
                    <SectionBody>
                        <RequestRow name='Practice Center:' value={basicInfo.centerName} />
                        <RequestRow name='Location:' value={basicInfo.centerAddress} />
                        <RequestRow name='City:' value={basicInfo.centerCity} />
                        <RequestRow name='Locality:' value={basicInfo.centerLocality} />
                    </SectionBody>
                </SectionInnerContainer>
            </SectionContainer>

            <SectionContainer>
                <SectionInnerContainer>
                    <SectionHeader>SPOC Details</SectionHeader>
                    <SectionBody>
                        <SpocDetailContainer>
                            <RequestRow name='Name:' value={`${basicInfo.name} (${basicInfo.type})`} />
                            <RequestRow name='Email:' value={basicInfo.email} />
                            <RequestRow name='Phone:' value={basicInfo.phone} />
                            <RequestRow name='Comm. Type:' value={basicInfo.commType} />
                            <RequestRow name='Send WhatsApp:' value={yesNo(basicInfo.sendWhatsApp)} />
                            <RequestRow name='Send Email:' value={yesNo(basicInfo.sendEmail)} />
                            <RequestRow name='Send CC:' value={yesNo(basicInfo.sendCC)} />
                            <RequestRow name='Send SMS:' value={yesNo(basicInfo.sendSMS)} />
                            <RequestRow name='Send Call:' value={yesNo(basicInfo.sendCall)} />
                        </SpocDetailContainer>
                    </SectionBody>
                </SectionInnerContainer>
            </SectionContainer>
        </DetailsContainer>
    )

    const handleGenerateCashlessLetter = () => {
        const { requestId } = details
        setGenerateLetterLoading(true)
        return generateCashlessLetter(requestId)
            .then((response) => {
                toast.success(response.message)
            })
            .catch((err) => {
                const errorMessage = err.message || 'Error while generating cashless letter';
                toast.error(errorMessage)
            })
            .finally(() => {
                setGenerateLetterLoading(false)
            })
    }

    const renderActions = () => (
        <DetailsContainer>
            <SectionContainer>
                <SectionInnerContainer>
                    <SectionHeader>Actions</SectionHeader>
                    <SectionBody>
                        <Button
                            color="primary"
                            onClick={handleGenerateCashlessLetter}
                            disabled={generateLetterLoading}
                            style={{ marginBottom: '10px' }}
                        >
                            {generateLetterLoading ? 'Generating...' : 'Generate Cashless Letter'}
                        </Button>
                        {basicInfo.cashlessLetterUrl && (
                            <Button
                                color="success"
                                onClick={async () => { const blobUrl = await getBlobUrl(basicInfo.cashlessLetterUrl); window.open(blobUrl, '_blank'); }}
                                style={{ marginBottom: '10px' }}
                            >
                                Preview Cashless Letter
                            </Button>
                        )}
                    </SectionBody>
                </SectionInnerContainer>
            </SectionContainer>
        </DetailsContainer>
    )

    const renderUploadedDocuments = () => {
        const sessionUrls = Array.isArray(basicInfo.sessionPrescriptionUrls) ? basicInfo.sessionPrescriptionUrls : [];
        const consultationUrls = Array.isArray(basicInfo.consultationPrescriptionUrls) ? basicInfo.consultationPrescriptionUrls : [];

        if (sessionUrls.length === 0 && consultationUrls.length === 0) {
            return (
                <DetailsContainer>
                    <SectionContainer>
                        <SectionInnerContainer>
                            <SectionHeader>Uploaded Documents</SectionHeader>
                            <SectionBody>
                                <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
                                    No documents uploaded yet.
                                </div>
                            </SectionBody>
                        </SectionInnerContainer>
                    </SectionContainer>
                </DetailsContainer>
            );
        }

        const renderDocCards = (urls) => (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '8px' }}>
                {urls.map((url, idx) => (
                    <div
                        key={idx}
                        onClick={async () => { const blobUrl = await getBlobUrl(url); window.open(blobUrl, '_blank'); }}
                        style={{
                            width: '100px',
                            padding: '12px 8px',
                            backgroundColor: '#F8F9FA',
                            borderRadius: '8px',
                            border: '1px solid #E0E0E0',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#E7E3FF';
                            e.currentTarget.style.borderColor = '#714FFF';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(113,79,255,0.2)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#F8F9FA';
                            e.currentTarget.style.borderColor = '#E0E0E0';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    >
                        <span style={{ fontSize: '2rem' }}>📄</span>
                        <span style={{ fontSize: '0.72rem', color: '#4b4f5b', fontWeight: '500', textAlign: 'center' }}>
                            {`Document ${idx + 1}`}
                        </span>
                        <span style={{ fontSize: '0.65rem', color: '#714FFF' }}>Click to open</span>
                    </div>
                ))}
            </div>
        );

        return (
            <DetailsContainer>
                <SectionContainer>
                    {sessionUrls.length > 0 && (
                        <SectionInnerContainer>
                            <SectionHeader>Session Prescriptions</SectionHeader>
                            <SectionBody>
                                {renderDocCards(sessionUrls)}
                            </SectionBody>
                        </SectionInnerContainer>
                    )}
                    {consultationUrls.length > 0 && (
                        <SectionInnerContainer>
                            <SectionHeader>Consultation Prescriptions</SectionHeader>
                            <SectionBody>
                                {renderDocCards(consultationUrls)}
                            </SectionBody>
                        </SectionInnerContainer>
                    )}
                </SectionContainer>
            </DetailsContainer>
        );
    }

    const handleRequestSubmit = () => {
        const { requestId } = details
        return requestPhysiotherapyPayment(requestId)
        .then((response) => {
            console.log(response);
            if (response.message === 'success') {
                return toast.success('Payout requested successfully')
            }
            toast.error(response.errorMessage);
        })
        .catch((err) => {
            toast.error('Error while requesting payout')
        })
        .finally(() => {
            setShowConfirmModal(false)
            setReqPaymentLoading(false)
        })
    }

    if (!isOpen) {
        return <></>
    }

    if (!basicInfo || !details.requestId) {
        return <LoadingComponent color="black" />;
    }

    if (basicInfo && details.requestId) {
        return <Modal
            isOpen={details.showRequestDetails}
            toggle={onClose}
            size='lg'
            className='rtsp-modal-big'
        >
            <ModalHeader
                className='bg-primary'
                toggle={onClose}
            >
                {`Request Details (${details.requestId})`}
            </ModalHeader>
            <ModalBody>
                <Container fluid>
                    <div className='row'>
                        <ButtonGroup>
                            <Button
                                onClick={() => setActiveInfo('basic')}
                                active={activeInfo === 'basic'}
                            >
                                Basic Info
                            </Button>
                            <Button
                                onClick={() => setActiveInfo('center')}
                                active={activeInfo === 'center'}
                            >
                                Center Info
                            </Button>
                            <Button
                                onClick={() => setActiveInfo('actions')}
                                active={activeInfo === 'actions'}
                            >
                                Actions
                            </Button>
                            <Button
                                onClick={() => setActiveInfo('documents')}
                                active={activeInfo === 'documents'}
                            >
                                Uploaded Documents
                            </Button>
                        </ButtonGroup>
                    </div>
                    <div className='row'>
                        <div className='col-md-8'>
                            {activeInfo === 'basic' && renderBasicInfo()}
                            {activeInfo === 'center' && renderCenterInfo()}
                            {activeInfo === 'actions' && renderActions()}
                            {activeInfo === 'documents' && renderUploadedDocuments()}
                        </div>
                        <div className='col-md-4'>
                            {renderTimeline()}
                        </div>
                    </div>
                    <ConfirmationModal
                        isOpen={showConfirmModal}
                        confirmationMessage={confirmMessage}
                        callback={handleRequestSubmit}
                        toggle={() => setShowConfirmModal(prev => !prev)}
                    />
                </Container>
            </ModalBody>
        </Modal>
    } else {
        return <div />
    }
}

export default PhysiotherapyRequestDetails;
